import { Injectable } from '@angular/core';
import { defer, EMPTY, filter, map, merge, Observable, of, shareReplay, startWith, switchMap, take, tap } from 'rxjs';

import { ConfigFeatures, FeatureFlagsConfigService } from '@olympus/ngx-shared';
import { CryptlexLicense } from 'electron-app/licenses';
import { DialogService } from 'shared-modules-angular/dialog';

import { ConfigService } from '../../../core/services/config';
import { PcLicenseModalComponent, PcLicenseModalDialogNotification } from '../components/pc-license.component';
import { CryptlexLicenseService, LicenseStatusIndicator } from './cryptlex-license.service';
import { EvCLicenseService } from './evc-license.service';

export type { LicenseStatusIndicator } from './cryptlex-license.service';

/**
 * Single entry point the application uses for licensing,
 * hiding whether the active mode is Evident Connect (web), Cryptlex (PC) or none.
 */
@Injectable({
  providedIn: 'root',
})
export class LicenseFacadeService {
  /** Header badge state (days-left / grace period / imminent lock). */
  public readonly statusIndicator$: Observable<LicenseStatusIndicator>;

  /** Emits whenever license-derived feature flags must be re-applied. */
  public readonly featuresChanged$: Observable<void>;

  /** Shared validation so it runs once regardless of how many callers subscribe. */
  private _licensed$: Observable<void> | undefined;

  private get isPc(): boolean {
    return this._configService.licenseType === 'PC';
  }

  constructor(
    private readonly _configService: ConfigService,
    private readonly _cryptlexService: CryptlexLicenseService,
    private readonly _evcService: EvCLicenseService,
    private readonly _featureFlagsService: FeatureFlagsConfigService,
    private readonly _dialogService: DialogService,
  ) {
    // The Cryptlex subjects stay at their seeded defaults in non-PC modes (getLicense is never called),
    this.statusIndicator$ = this._cryptlexService.handleLicenseStatusIndicator$.pipe(
      startWith(this._cryptlexService.defaultLicenseStatusIndicator),
    );

    this.featuresChanged$ = this._cryptlexService.handleLicense$.pipe(
      filter(license => !!license),
      tap(license => this._featureFlagsService.updateFeatures(license.features as ConfigFeatures)),
      map(() => undefined),
      startWith(undefined),
    );
  }

  /**
   * Ensures that the application has a valid license before emitting.
   * The validation runs once and its result is shared across all callers.
   */
  public ensureLicensed(): Observable<void> {
    this._licensed$ ??= this.validateLicense().pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this._licensed$;
  }

  /**
   * Opens the PC license modal on demand. No-op outside PC mode.
   */
  public openLicenseModal(): void {
    if (!this.isPc) {
      return;
    }

    const notification: PcLicenseModalDialogNotification = { licenseStatus: undefined, promptRestartOnLicenseChange: true };
    this._dialogService.open(
      PcLicenseModalComponent,
      {
        data: notification,
        width: PcLicenseModalComponent.WIDTH,
        height: PcLicenseModalComponent.HEIGHT,
        disableClose: true,
      },
    );
  }

  private validateLicense(): Observable<void> {
    if (this._configService.checkLicenseWeb) {
      return this._evcService.isLicenseValid().pipe(
        filter(valid => valid),
        map(() => undefined),
        take(1),
      );
    }

    if (this.isPc) {
      return this.ensurePcLicensed();
    }

    return of(undefined);
  }

  private ensurePcLicensed(): Observable<void> {
    const notification: PcLicenseModalDialogNotification = { licenseStatus: undefined, promptRestartOnLicenseChange: false };

    const licensed$: Observable<void> = this._cryptlexService.handleLicense$.pipe(
      filter(license => !!license),
      tap((license: CryptlexLicense) => this._featureFlagsService.updateFeatures(license.features as ConfigFeatures)),
      take(1),
      switchMap((license: CryptlexLicense) => {
        if (license.status === 'activated' || this._cryptlexService.isInGracePeriod) {
          return of(undefined);
        }

        return this._dialogService.open<PcLicenseModalDialogNotification, PcLicenseModalDialogNotification, PcLicenseModalComponent>(
          PcLicenseModalComponent,
          {
            data: notification,
            width: PcLicenseModalComponent.WIDTH,
            height: PcLicenseModalComponent.HEIGHT,
            disableClose: true,
          },
        ).closed.pipe(
          filter(result => result?.licenseStatus === 'activated' || this._cryptlexService.isInGracePeriod),
          map(() => undefined),
          take(1),
        );
      }),
    );

    // merge subscribes to licensed$ before the defer runs getLicense, so the initial emission is never missed.
    return merge(
      licensed$,
      defer(() => {
        this._cryptlexService.getLicense();

        return EMPTY;
      }),
    );
  }
}
